import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import axios from 'axios';
import { AuthProvider } from './lib/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import RoleSelection from './pages/RoleSelection';
import UserSignUp from './pages/UserSignUp';
import CompanySignUp from './pages/CompanySignUp';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyPurchase from './pages/VerifyPurchase';
import BuyPage from './pages/BuyPage';
import HubBuyPage from './pages/HubBuyPage';
import SlugPage from './pages/SlugPage';
import CompanyDashboard from './pages/CompanyDashboard';
import TokenPurchase from './pages/TokenPurchase';
import AdminPage from './pages/AdminPage';
import AdminAddCompany from './pages/AdminAddCompany';
import LandingPage5 from './pages/LandingPage5';
import LocalNews from './pages/LocalNews';
import GenreFeed from './pages/GenreFeed';
import AuthorDashboard from './pages/AuthorDashboard';
import EditorDashboard from './pages/EditorDashboard';
import NavbarRoute from './pages/NavbarRoute';
import AdvertisePage from './pages/AdvertisePage';
import LandingPage7 from './pages/LandingPage7';
import LandingPage8 from './pages/LandingPage8';
import HelpCenter from './pages/HelpCenter';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import NotFoundPage from './pages/NotFoundPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ProtectedRoute from './components/ProtectedRoute';
import CompanyContact from './pages/CompanyContact';
import TestLinks from './pages/TestLinks';
import './App.css';
import 'bootstrap/dist/css/bootstrap.min.css';

function App() {
  useEffect(() => {
    let sid = sessionStorage.getItem('pubsilo_sid');
    if (!sid) {
      sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem('pubsilo_sid', sid);
    }
    axios.post('/api/analytics/visit', { sessionId: sid }).catch(() => {});
  }, []);

  return (
    <HelmetProvider>
    <ErrorBoundary>
    <AuthProvider>
    <Router>
      <Routes>
        {/* Auth Routes */}
        <Route path="/" element={<LandingPage5 />} />
        <Route path="/signup/role" element={<RoleSelection />} />
        <Route path="/company-contact" element={<CompanyContact />} />
        <Route path="/signup/reader" element={<UserSignUp />} />
        <Route path="/signup/author" element={<CompanySignUp />} />
        <Route path="/signup/company" element={<CompanySignUp />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify" element={<VerifyPurchase />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/buy" element={<BuyPage />} />
        <Route path="/hub-buy" element={<HubBuyPage />} />

        {/* Company Admin Hub — must be defined before /:slug to avoid conflict */}
        <Route
          path="/adminHUD/:slug"
          element={
            <ProtectedRoute>
              <CompanyDashboard />
            </ProtectedRoute>
          }
        />

        {/* Token purchase page */}
        <Route
          path="/tokens/purchase"
          element={
            <ProtectedRoute>
              <TokenPurchase />
            </ProtectedRoute>
          }
        />

        {/* Site admin page */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/add-company"
          element={
            <ProtectedRoute>
              <AdminAddCompany />
            </ProtectedRoute>
          }
        />

        {/* Local news full feed */}
        <Route path="/local-news" element={<LocalNews />} />

        {/* Genre feed pages */}
        <Route path="/national-news" element={<GenreFeed genre="National News" />} />
        <Route path="/state-news"    element={<GenreFeed genre="State News" />} />
        <Route path="/sports"        element={<GenreFeed genre="Sports" />} />
        <Route path="/technology"    element={<GenreFeed genre="Technology" />} />

        {/* Embedded link design lab — no auth required */}
        <Route path="/testlinks" element={<TestLinks />} />

        <Route path="/landing5" element={<LandingPage5 />} />
        <Route path="/navbarroute" element={<NavbarRoute />} />
        <Route path="/advertise" element={<AdvertisePage />} />
        <Route path="/landing7" element={<LandingPage7 />} />
        <Route path="/landingpage8" element={<LandingPage8 />} />
        <Route path="/help" element={<HelpCenter />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />

        {/* Author dashboard — must be before /:slug to avoid conflict */}
        <Route
          path="/:companyName/author/:username"
          element={
            <ProtectedRoute>
              <AuthorDashboard />
            </ProtectedRoute>
          }
        />

        {/* Editor dashboard */}
        <Route
          path="/:companyName/editor/:username"
          element={
            <ProtectedRoute>
              <EditorDashboard />
            </ProtectedRoute>
          }
        />

        {/* /:slug resolves to user's own hub (Dashboard) or a public company page */}
        <Route path="/:slug" element={<SlugPage />} />

        {/* Catch-all 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Router>
    </AuthProvider>
    </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
